import React, { useState, useEffect, Fragment } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import { PreviewScheduleResponse } from '@beable/roster-backend-client-v1'
import { Box, Grid, List, ListItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatInTimeZone } from 'date-fns-tz';

import { getStyles } from '../styles';
import ContentCourseDetails from '../../Common/Selectors/ContentCourseDetails';
import { CourseSchedule, ScheduleKey } from '../../../interfaces/Schedule';
import { useCourseState } from '../../../context/CourseContext';

const colors = [
  '#0074A1', '#F97E06', '#2ECE9A', '#C5E855',
  '#C4B500', '#3AA3D2', '#BEA5E5', '#E9015B',
]

interface DeliveryScheduleProps {
  previewSchedule: PreviewScheduleResponse;
}

export default function DeliverySchedule({ previewSchedule }: DeliveryScheduleProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [courseSchedules, setCourseSchedules] = useState<CourseSchedule[]>([]);
  const [scheduleKey, setScheduleKey] = useState<ScheduleKey[]>([]);
  const { site } = useCourseState();

  useEffect(() => {
    const getLessonName = (lessonId: string) => {
      const lesson = previewSchedule.lessonTitles.find(lesson => lesson.lessonId === lessonId);
      return lesson?.lessonName;
    }

    const getCourse = (courseId: string) => {
      const course = previewSchedule.courses.find(course => course.courseId === courseId);
     
      return course;
    }

    const getSchedules = () => {
      const schedules = new Array<CourseSchedule>();
      const key: ScheduleKey[] = [];

      previewSchedule.schedule.forEach((schedule) => {
        schedule.assignments.forEach(assignment => {
          let courseSchedule = schedules.find(s => (
            s.academicYearId === schedule.academicYearId && s.courseId === assignment.courseId
          ));

          if (courseSchedule) {
            schedules.splice(schedules.indexOf(courseSchedule), 1);
          } else {
            courseSchedule = {} as CourseSchedule;
            courseSchedule.academicYearId = schedule.academicYearId;
            courseSchedule.courseId = assignment.courseId;
            courseSchedule.events = [];
            courseSchedule.color = colors[schedules.length];

            const course = getCourse(courseSchedule.courseId);
            if(course){
              key.push({
                course,
                color: courseSchedule.color
              })
            }
          }

          courseSchedule.events.push({
            title: getLessonName(assignment.lessonId) ?? '',
            start: formatInTimeZone(assignment.startDt, site?.timezone ?? '', 'yyyy-MM-dd'),
          })

          schedules.push(courseSchedule);
        })
      })

      setCourseSchedules(schedules);
      setScheduleKey(key);
    }

    if (previewSchedule.schedule) {
      getSchedules();
    }
  }, [previewSchedule, site]);

  function renderInnerContent( innerProps: any ) {
    return (
      <div className='fc-event-main-frame'>
        { innerProps.timeText &&
          <div className='fc-event-time'>{ innerProps.timeText }</div>
        }
        <div className='fc-event-title-container'>
          <div className='fc-event-title fc-sticky'>
            { innerProps.event.title || <Fragment>&nbsp;</Fragment> }
          </div>
        </div>
      </div>
    );
  }

  return (
    <Grid container sx={{
      alignItems: 'center'
    }}>
      <Grid size={3}>
        <Typography>
            Courses
        </Typography>
        <List dense sx={styles.previewSection}>
          { scheduleKey.map((keyItem) => (
          <ListItem key={`${keyItem.course.courseId}-${keyItem.course.locationCode}`} sx={styles.paddingRL}>
            <Box sx={[styles.keyColorBox, { backgroundColor: keyItem.color }]} />
            <ContentCourseDetails course={keyItem.course}/>
          </ListItem>
          ))}
        </List>
      </Grid>
      <Grid sx={styles.previewSection} size={9}>
        <FullCalendar
          plugins={[ dayGridPlugin ]}
          initialView="dayGridMonth"
          eventSources={courseSchedules}
          eventContent={ ( arg ) => {
            return (
              <Tooltip title={ <Typography variant="body2">{arg.event.title}</Typography> } arrow>
                { renderInnerContent(arg) }
              </Tooltip>
            );
          }}
          // views ={{
          //   dayGrid: {
          //     type: 'dayGrid',  // Applicable for: dayGrid and dayGridMonth
          //     duration: { months: 3}, // Ex: Current month + (current)next month
          //   }
          // }}
          // fixedWeekCount={false}
        />
      </Grid>
    </Grid>
  );
}